import { DeleteOutline } from "@mui/icons-material";
import { Button, Stack } from "@mui/material";
import {
  useCompanyDataServiceV2,
  useUserManagementServiceV1,
} from "@rw1/api-services";
import { UserManagement_V1 } from "@rw1/auto-generated-clients";
import { useMutation } from "@tanstack/react-query";
import { KeyboardEvent, useEffect, useRef, useState } from "react";
import { LabeledSwitch } from "../../../components/LabeledSwitch";
import { LabelValue } from "../../../components/LabelValue";
import { TextFieldWithCounter } from "../../../components/TextFieldWithCounter";
import { useCompanyDefaultUserGroups } from "../../../hooks/useCompanyDefaultUserGroups";
import { useLocalization } from "../../../hooks/useLocalization";

interface OverviewTabProps {
  groupId?: string;
  userGroup?: UserManagement_V1.UserGroup;
  isPrimaryGroup: boolean;
  onDeleteGroup: () => void;
  refetchUserGroup: () => void;
  invalidateUserGroup: () => void;
  refetchDefaultUserGroups: () => void;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({
  groupId,
  userGroup,
  isPrimaryGroup,
  onDeleteGroup,
  refetchUserGroup,
  invalidateUserGroup,
  refetchDefaultUserGroups,
}) => {
  const { t } = useLocalization();

  const userManagementClient = useUserManagementServiceV1();
  const companyDataClient = useCompanyDataServiceV2();

  // Get default user groups for the company
  const companyId = userGroup?.companyId;
  const { defaultUserGroups, isLoading: isDefaultGroupsLoading } =
    useCompanyDefaultUserGroups(companyId);
  const isDefaultGroup = defaultUserGroups.some(
    (g) => g.userGroupId === groupId,
  );

  // Optimistic state: gives instant visual feedback without disabling the switch
  const [optimisticIsDefault, setOptimisticIsDefault] = useState<
    boolean | undefined
  >(undefined);

  // Clear optimistic state once server data has caught up
  useEffect(() => {
    if (
      optimisticIsDefault !== undefined &&
      optimisticIsDefault === isDefaultGroup
    ) {
      setOptimisticIsDefault(undefined);
    }
  }, [isDefaultGroup, optimisticIsDefault]);

  const toggleDebounceRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // Mutation to add/remove this group as a default group
  const toggleDefaultGroupMutation = useMutation({
    mutationFn: async (checked: boolean) => {
      if (!companyId || !groupId) return;
      if (checked) {
        await (
          companyDataClient.addDefaultCompanyUserGroup as (
            companyId: string,
            body: string,
          ) => Promise<void>
        )(companyId, groupId);
      } else {
        await companyDataClient.deleteDefaultCompanyUserGroup(
          companyId,
          groupId,
        );
      }
    },
    onSuccess: () => {
      refetchUserGroup();
      refetchDefaultUserGroups();
    },
    onError: () => {
      // Revert to last known server value on failure
      setOptimisticIsDefault(undefined);
    },
  });

  const [description, setDescription] = useState<string>("");
  const [descriptionTimeoutId, setDescriptionTimeoutId] = useState<
    NodeJS.Timeout | undefined
  >();

  useEffect(() => {
    if (userGroup?.description) {
      setDescription(userGroup.description);
    }
  }, [userGroup?.description]);

  const handleDescriptionChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newDescription = event.target.value;
    setDescription(newDescription);

    // Clear existing timeout
    if (descriptionTimeoutId) {
      clearTimeout(descriptionTimeoutId);
    }

    // Set new timeout to auto-save after 500ms of inactivity
    const timeoutId = setTimeout(() => {
      updateDescriptionMutation.mutate(newDescription);
    }, 500);

    setDescriptionTimeoutId(timeoutId);
  };

  // cleanup the toggle debounce on unmount
  useEffect(() => {
    return () => {
      if (toggleDebounceRef.current) {
        clearTimeout(toggleDebounceRef.current);
      }
    };
  }, []);

  // cleanup description timeout on unmount
  useEffect(() => {
    return () => {
      if (descriptionTimeoutId) {
        clearTimeout(descriptionTimeoutId);
      }

      // we invalidate the user group while unmounting to ensure any pending description changes are
      // reflected when the user navigates back.
      // if we invalidate the query on success it will cause issues since
      // the data will be re-fetched constantly while typing causing a bad user experience.
      invalidateUserGroup();
      refetchDefaultUserGroups();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateDescriptionMutation = useMutation({
    mutationFn: (newDescription: string) => {
      if (!groupId || !userGroup?.companyId) {
        return Promise.reject(
          new Error(
            "Group ID and Company ID are required to update description.",
          ),
        );
      }

      return userManagementClient.updateUserGroup(
        groupId,
        new UserManagement_V1.UserGroupWithoutId({
          userGroupName: userGroup.userGroupName,
          description: newDescription,
          companyId: userGroup.companyId,
        }),
      );
    },
  });

  const handleNoNewLine = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  const handleDefaultGroupToggle = (
    _: React.SyntheticEvent,
    checked: boolean,
  ) => {
    setOptimisticIsDefault(checked);
    if (toggleDebounceRef.current) {
      clearTimeout(toggleDebounceRef.current);
    }
    toggleDebounceRef.current = setTimeout(() => {
      toggleDefaultGroupMutation.mutate(checked);
    }, 300);
  };

  return (
    <Stack spacing={{ xs: "24px", md: "8px" }}>
      <LabelValue
        boxSx={{ maxWidth: 496, width: "100%" }}
        containerSx={{
          minHeight: "88px",
        }}
        label={t("groups.overviewTab.description")}
      >
        <TextFieldWithCounter
          isDisabled={isPrimaryGroup}
          multiline={true}
          onChange={handleDescriptionChange}
          onKeyDown={handleNoNewLine}
          placeholder={t("groups.descriptionPlaceholder")}
          rows={2}
          value={description}
        />
      </LabelValue>

      <LabelValue
        label={t("groups.overviewTab.users")}
        value={userGroup?.userCount}
      />
      <LabelValue
        label={t("groups.overviewTab.experiences")}
        value={userGroup?.experienceCount}
      />
      <LabeledSwitch
        checked={optimisticIsDefault ?? isDefaultGroup}
        color="primary"
        disabled={isPrimaryGroup || isDefaultGroupsLoading}
        label={t("groups.overviewTab.defaultGroup")}
        onChange={handleDefaultGroupToggle}
        tooltip={
          isPrimaryGroup
            ? t("groups.overviewTab.primaryGroupTooltip")
            : t("groups.overviewTab.defaultGroupTooltip")
        }
      />

      {!isPrimaryGroup && (
        <LabelValue
          containerSx={{ pt: "48px", height: "88px" }}
          label={t("groups.overviewTab.deleteGroup")}
        >
          <Button onClick={onDeleteGroup} variant="secondary">
            <DeleteOutline sx={{ fontSize: 24, mr: 1 }} />
            {t("groups.overviewTab.delete")}
          </Button>
        </LabelValue>
      )}
    </Stack>
  );
};
